import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Anchor,
  Button,
  Card,
  Checkbox,
  Divider,
  Grid,
  Group,
  Loader,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import {
  IconAlertCircle,
  IconDownload,
  IconReceipt,
} from "@tabler/icons-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import useTransientStore from "../../../data/transcientStore";
import { useTranslation } from "../../../hooks/useTranslation";
import { usePermissions } from "../../../hooks/usePermissions";
import { ReportSectionHeader } from "../ReportSectionHeader";
import { getContractsByAddressSystem } from "../../../services/ContractService";
import {
  generateInvoicedReport,
  getInvoicedReportOptions,
} from "../../../services/InvoicedReportService";
import { ContractListItem } from "../../../types/UserRole";
import {
  InvoicedReportOptionsResponse,
  InvoicedReportResponse,
  InvoicedReportRow,
} from "../../../types/InvoicedReport";

interface InvoicedReportProps {
  onBack: () => void;
  showHeader?: boolean;
}

const DATE_FORMAT = "DD.MM.YYYY";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("fi-FI", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value ?? 0);

const formatDate = (value?: string | null) =>
  value ? dayjs(value).format(DATE_FORMAT) : "";

export default function InvoicedReport({
  onBack,
  showHeader = true,
}: Readonly<InvoicedReportProps>) {
  const selectedAddressSystemId =
    useTransientStore((state) => state.selectedAddressSystem?.id) ?? 0;
  const currentUserId = useTransientStore((state) => state.currentUserId);
  const { getTranslation } = useTranslation({
    pageId: 516,
    addressSystemId: selectedAddressSystemId,
    autoLoad: true,
  });
  const { checkAccess } = usePermissions();

  const [contracts, setContracts] = useState<ContractListItem[]>([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [contractStatus, setContractStatus] = useState<"active" | "archived">(
    "active",
  );
  const [selectedContractIds, setSelectedContractIds] = useState<string[]>([]);

  const [options, setOptions] = useState<InvoicedReportOptionsResponse | null>(
    null,
  );
  const [optionsLoading, setOptionsLoading] = useState(false);

  const [selectedStatusIds, setSelectedStatusIds] = useState<string[]>([]);
  const [location, setLocation] = useState("");
  const [subscriberId, setSubscriberId] = useState<string | null>(null);
  const [foremanId, setForemanId] = useState<string | null>(null);
  const [typeId, setTypeId] = useState<string | null>(null);
  const [qualityControlId, setQualityControlId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const [loadError, setLoadError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [report, setReport] = useState<InvoicedReportResponse | null>(null);

  useEffect(() => {
    if (!selectedAddressSystemId) {
      setContracts([]);
      setOptions(null);
      return;
    }

    const load = async () => {
      setContractsLoading(true);
      setOptionsLoading(true);
      setLoadError(null);

      const describeError = (label: string, error: unknown) => {
        const axiosError = error as {
          response?: { status?: number; data?: unknown };
          message?: string;
        };
        const data = axiosError.response?.data;
        const serverMessage =
          typeof data === "string"
            ? data
            : (data as { message?: string })?.message ??
              (data ? JSON.stringify(data) : undefined);
        const status = axiosError.response?.status;
        return `${label} failed${status ? ` (HTTP ${status})` : ""}: ${
          serverMessage ?? axiosError.message ?? "unknown error"
        }`;
      };

      try {
        const contractItems = await getContractsByAddressSystem(
          selectedAddressSystemId,
        ).catch((error) => {
          throw new Error(describeError("Contracts", error));
        });
        const optionData = await getInvoicedReportOptions(
          selectedAddressSystemId,
          currentUserId,
        ).catch((error) => {
          throw new Error(describeError("Report options", error));
        });
        setContracts(contractItems);
        setOptions(optionData);
        setSelectedStatusIds(optionData.defaultStatusIds.map(String));
      } catch (error) {
        console.error("Failed to load invoiced report data", error);
        const baseMessage =
          getTranslation("REPORTS_CONTRACTS_LOAD_ERROR") ||
          "Unable to load report filters for the address system.";
        const detail = error instanceof Error ? error.message : undefined;
        setLoadError(detail ? `${baseMessage} — ${detail}` : baseMessage);
      } finally {
        setContractsLoading(false);
        setOptionsLoading(false);
      }
    };

    load();
  }, [selectedAddressSystemId, currentUserId]);

  const filteredContracts = useMemo(() => {
    return contracts.filter((contract) =>
      contractStatus === "active"
        ? contract.contractStatusDefaultValue === 1
        : contract.contractStatusDefaultValue !== 1,
    );
  }, [contracts, contractStatus]);

  // Preselect all visible contracts by default when the list changes.
  useEffect(() => {
    setSelectedContractIds(filteredContracts.map((c) => String(c.id)));
  }, [filteredContracts]);

  const toggleAllContracts = (checked: boolean) => {
    setSelectedContractIds(
      checked ? filteredContracts.map((c) => String(c.id)) : [],
    );
  };

  const handleGenerate = async () => {
    if (selectedContractIds.length === 0) {
      setGenerationError(
        getTranslation("REPORTS_INVOICED_NO_CONTRACT") ||
          "Select at least one contract.",
      );
      return;
    }
    if (selectedStatusIds.length === 0) {
      setGenerationError(
        getTranslation("REPORTS_INVOICED_NO_STATUS") ||
          "Select at least one status.",
      );
      return;
    }

    setGenerationError(null);
    setGenerating(true);
    try {
      const result = await generateInvoicedReport({
        addressSystemId: selectedAddressSystemId,
        currentUserId,
        contractIds: selectedContractIds.map(Number),
        statusIds: selectedStatusIds.map(Number),
        location: location.trim() || null,
        subscriberId: subscriberId ? Number(subscriberId) : null,
        foremanId: foremanId ? Number(foremanId) : null,
        typeId: typeId ? Number(typeId) : null,
        qualityControlId: qualityControlId ? Number(qualityControlId) : null,
        startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
        endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
      });
      setReport(result);
    } catch (error) {
      console.error("Failed to generate invoiced report", error);
      setGenerationError(
        getTranslation("REPORTS_GENERATE_ERROR") ||
          "Failed to generate the report. Please try again.",
      );
    } finally {
      setGenerating(false);
    }
  };

  const columnLabels = () => ({
    location: getTranslation("REPORTS_COLUMN_LOCATION") || "Location",
    contract: getTranslation("REPORTS_COLUMN_CONTRACT") || "Contract",
    status: getTranslation("REPORTS_COLUMN_STATUS") || "Status",
    type: getTranslation("REPORTS_COLUMN_TYPE") || "Type",
    foreman: getTranslation("REPORTS_COLUMN_FOREMAN") || "Foreman",
    plannedDate: getTranslation("REPORTS_COLUMN_PLANNED_DATE") || "Planned date",
    actual: getTranslation("REPORTS_COLUMN_ACTUAL_PRICE") || "Actual price",
    invoiced: getTranslation("REPORTS_COLUMN_INVOICED_PRICE") || "Invoiced price",
    hold: getTranslation("REPORTS_COLUMN_HOLD_PRICE") || "Hold price",
    permit: getTranslation("REPORTS_COLUMN_INVOICE_PERMIT") || "Invoice permit",
  });

  const buildRowArray = (row: InvoicedReportRow, labels: ReturnType<typeof columnLabels>) => [
    row.location || "",
    row.contractName || "",
    row.statusLabel || "",
    row.typeLabel || "",
    row.foremanName || "",
    formatDate(row.plannedDate),
    formatCurrency(row.actualPrice),
    formatCurrency(row.invoicedPrice),
    formatCurrency(row.holdPrice),
    row.allowedToInvoice
      ? getTranslation("REPORTS_YES") || "Yes"
      : getTranslation("REPORTS_NO") || "No",
  ];

  const handleDownloadCsv = () => {
    if (!report) return;
    const labels = columnLabels();
    const header = [
      labels.location,
      labels.contract,
      labels.status,
      labels.type,
      labels.foreman,
      labels.plannedDate,
      labels.actual,
      labels.invoiced,
      labels.hold,
      labels.permit,
    ];
    const worksheet = XLSX.utils.aoa_to_sheet([
      header,
      ...report.rows.map((row) => buildRowArray(row, labels)),
    ]);
    const csv = XLSX.utils.sheet_to_csv(worksheet, { FS: ";" });
    const blob = new Blob([`\uFEFF${csv}`], {
      type: "text/csv;charset=utf-8;",
    });
    saveAs(blob, `invoiced-report-${dayjs().format("YYYYMMDD")}.csv`);
  };

  if (!checkAccess("CITY_OBJECT_INVOICE_REPORT")) {
    return (
      <Card withBorder radius="lg" px="xl" py="lg" shadow="sm">
        <Alert color="yellow">
          {getTranslation("REPORTS_NO_ACCESS") ||
            "You do not have permission to view this report."}
        </Alert>
      </Card>
    );
  }

  const labels = columnLabels();
  const isBusy = contractsLoading || optionsLoading;

  return (
    <Card withBorder radius="lg" px="xl" py="lg" shadow="sm">
      <Stack gap="md">
        {showHeader && (
          <ReportSectionHeader
            onBack={onBack}
            contextLabel={getTranslation("REPORTS_HUB_LABEL") || "Reports"}
            title={
              getTranslation("REPORTS_INVOICE_REPORT_TITLE") ||
              "Search for invoiced objects"
            }
            backLabel={getTranslation("REPORTS_RETURN") || "Back to reports"}
            leadingVisual={
              <ThemeIcon size="xl" radius="xl" color="blue" variant="light">
                <IconReceipt size={24} />
              </ThemeIcon>
            }
          />
        )}

        {showHeader && <Divider variant="dashed" />}

        {loadError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {loadError}
          </Alert>
        )}

        {isBusy && (
          <Group gap="xs">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              {getTranslation("REPORTS_LOADING_FILTERS") ||
                "Loading filters..."}
            </Text>
          </Group>
        )}

        <Grid gutter="md">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="xs">
              <Group justify="space-between">
                <Text fw={600}>
                  {getTranslation("REPORTS_CONTRACT_LABEL") || "Contract:"}
                </Text>
                <Select
                  size="xs"
                  w={170}
                  value={contractStatus}
                  onChange={(value) =>
                    setContractStatus((value as "active" | "archived") ?? "active")
                  }
                  data={[
                    {
                      value: "active",
                      label:
                        getTranslation("REPORTS_ACTIVE_CONTRACTS") ||
                        "Active Contracts",
                    },
                    {
                      value: "archived",
                      label:
                        getTranslation("REPORTS_ARCHIVED_CONTRACTS") ||
                        "Archived Contracts",
                    },
                  ]}
                />
              </Group>
              {filteredContracts.length > 0 && (
                <Checkbox
                  size="xs"
                  label={getTranslation("REPORTS_SELECT_ALL") || "Select all"}
                  checked={
                    selectedContractIds.length === filteredContracts.length
                  }
                  indeterminate={
                    selectedContractIds.length > 0 &&
                    selectedContractIds.length < filteredContracts.length
                  }
                  onChange={(event) =>
                    toggleAllContracts(event.currentTarget.checked)
                  }
                />
              )}
              <Checkbox.Group
                value={selectedContractIds}
                onChange={setSelectedContractIds}
              >
                <ScrollArea.Autosize mah={220}>
                  <Stack gap={4}>
                    {filteredContracts.map((contract) => (
                      <Checkbox
                        key={contract.id}
                        value={String(contract.id)}
                        label={contract.name}
                      />
                    ))}
                  </Stack>
                </ScrollArea.Autosize>
              </Checkbox.Group>
              {filteredContracts.length === 0 && !isBusy && (
                <Text size="sm" c="dimmed">
                  {getTranslation("REPORTS_NO_CONTRACTS_FOR_STATUS") ||
                    "No contracts available for the selected status."}
                </Text>
              )}
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="sm">
              <TextInput
                label={getTranslation("REPORTS_INVOICED_NAME") || "Name:"}
                value={location}
                onChange={(event) => setLocation(event.currentTarget.value)}
              />
              {options && options.subscribers.length > 0 && (
                <Select
                  label={getTranslation("REPORTS_SUBSCRIBER") || "Subscriber:"}
                  placeholder={getTranslation("REPORTS_ALL") || "All"}
                  clearable
                  value={subscriberId}
                  onChange={setSubscriberId}
                  data={options.subscribers.map((s) => ({
                    value: String(s.id),
                    label: s.name,
                  }))}
                />
              )}
              <Select
                label={getTranslation("REPORTS_FOREMAN") || "Foreman:"}
                placeholder={getTranslation("REPORTS_ALL") || "All"}
                clearable
                value={foremanId}
                onChange={setForemanId}
                data={(options?.foremen ?? []).map((f) => ({
                  value: String(f.id),
                  label: f.name,
                }))}
              />
              <Select
                label={getTranslation("REPORTS_TYPE") || "Type:"}
                placeholder={getTranslation("REPORTS_ALL") || "All"}
                clearable
                value={typeId}
                onChange={setTypeId}
                data={(options?.types ?? []).map((t) => ({
                  value: String(t.id),
                  label: t.label,
                }))}
              />
              <Select
                label={
                  getTranslation("REPORTS_QUALITY_CONTROL") || "Quality control:"
                }
                placeholder={getTranslation("REPORTS_ALL") || "All"}
                clearable
                value={qualityControlId}
                onChange={setQualityControlId}
                data={(options?.qualityControls ?? []).map((q) => ({
                  value: String(q.id),
                  label: q.label,
                }))}
              />
              <Group grow>
                <DateInput
                  label={getTranslation("REPORTS_DATE_FROM") || "Date from:"}
                  valueFormat={DATE_FORMAT}
                  placeholder={DATE_FORMAT}
                  clearable
                  value={startDate}
                  onChange={setStartDate}
                />
                <DateInput
                  label={getTranslation("REPORTS_DATE_TO") || "Date to:"}
                  valueFormat={DATE_FORMAT}
                  placeholder={DATE_FORMAT}
                  clearable
                  value={endDate}
                  onChange={setEndDate}
                />
              </Group>
            </Stack>
          </Grid.Col>
        </Grid>

        <Stack gap="xs">
          <Text fw={600}>{getTranslation("REPORTS_STATUS") || "Status:"}</Text>
          <Checkbox.Group
            value={selectedStatusIds}
            onChange={setSelectedStatusIds}
          >
            <Group gap="md">
              {(options?.statuses ?? []).map((status) => (
                <Checkbox
                  key={status.id}
                  value={String(status.id)}
                  label={status.label}
                />
              ))}
            </Group>
          </Checkbox.Group>
        </Stack>

        {generationError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {generationError}
          </Alert>
        )}

        <Group>
          <Button
            onClick={handleGenerate}
            loading={generating}
            disabled={isBusy}
          >
            {getTranslation("REPORTS_SEARCH_BUTTON") || "Search"}
          </Button>
        </Group>

        {report && (
          <Stack gap="sm">
            <Divider />
            <Group justify="space-between">
              <Text fw={600}>
                {(getTranslation("REPORTS_RESULTS_FOUND") ||
                  "Search found {count} objects").replace(
                  "{count}",
                  String(report.totalCount),
                )}
              </Text>
              {report.totalCount > 0 && (
                <Anchor
                  component="button"
                  type="button"
                  onClick={handleDownloadCsv}
                >
                  <Group gap={4}>
                    <IconDownload size={16} />
                    {getTranslation("REPORTS_DOWNLOAD_CSV") ||
                      "Download as CSV"}
                  </Group>
                </Anchor>
              )}
            </Group>

            {report.totalCount > 0 && (
              <ScrollArea>
                <Table striped highlightOnHover withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{labels.location}</Table.Th>
                      <Table.Th>{labels.contract}</Table.Th>
                      <Table.Th>{labels.status}</Table.Th>
                      <Table.Th>{labels.type}</Table.Th>
                      <Table.Th>{labels.foreman}</Table.Th>
                      <Table.Th>{labels.plannedDate}</Table.Th>
                      <Table.Th ta="right">{labels.actual}</Table.Th>
                      <Table.Th ta="right">{labels.invoiced}</Table.Th>
                      <Table.Th ta="right">{labels.hold}</Table.Th>
                      <Table.Th>{labels.permit}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {report.rows.map((row) => (
                      <Table.Tr key={`${row.id}-${row.contractName}`}>
                        <Table.Td>{row.location}</Table.Td>
                        <Table.Td>{row.contractName}</Table.Td>
                        <Table.Td>{row.statusLabel}</Table.Td>
                        <Table.Td>{row.typeLabel}</Table.Td>
                        <Table.Td>{row.foremanName}</Table.Td>
                        <Table.Td>{formatDate(row.plannedDate)}</Table.Td>
                        <Table.Td ta="right">
                          {formatCurrency(row.actualPrice)}
                        </Table.Td>
                        <Table.Td ta="right">
                          {formatCurrency(row.invoicedPrice)}
                        </Table.Td>
                        <Table.Td ta="right">
                          {formatCurrency(row.holdPrice)}
                        </Table.Td>
                        <Table.Td>
                          {row.allowedToInvoice
                            ? getTranslation("REPORTS_YES") || "Yes"
                            : getTranslation("REPORTS_NO") || "No"}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                  <Table.Tfoot>
                    <Table.Tr>
                      <Table.Th colSpan={6} ta="right">
                        {getTranslation("REPORTS_TOTAL") || "Total"}
                      </Table.Th>
                      <Table.Th ta="right">
                        {formatCurrency(report.totalActualPrice)}
                      </Table.Th>
                      <Table.Th ta="right">
                        {formatCurrency(report.totalInvoicedPrice)}
                      </Table.Th>
                      <Table.Th ta="right">
                        {formatCurrency(report.totalHoldPrice)}
                      </Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Tfoot>
                </Table>
              </ScrollArea>
            )}

            {report.totalCount === 0 && (
              <Text size="sm" c="dimmed">
                {getTranslation("REPORTS_NO_RESULTS") ||
                  "The search returned no results."}
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
